import { ApplicationInsights, SeverityLevel } from '@microsoft/applicationinsights-web';

class AppInsightsService {
  private appInsights: ApplicationInsights | null = null;
  private initialized = false;

  /**
   * Initialize Application Insights SDK.
   */
  init(connectionString: string): void {
    if (this.initialized) {
      console.warn('[AppInsights] Already initialized');
      return;
    }

    if (!connectionString) {
      console.warn('[AppInsights] No connection string provided. Telemetry disabled.');
      return;
    }

    try {
      this.appInsights = new ApplicationInsights({
        config: {
          connectionString,
          enableAutoRouteTracking: true,
          enableCorsCorrelation: false,
          enableRequestHeaderTracking: false,
          enableResponseHeaderTracking: false,
          disableFetchTracking: false,
          disableAjaxTracking: false,
          correlationHeaderExcludedDomains: [
            '*.mixpanel.com',
            '*.contentsquare.net',
            '*.google-analytics.com',
          ],
        },
      });

      this.appInsights.loadAppInsights();

      // Set cloud role for Application Map
      this.appInsights.addTelemetryInitializer((envelope) => {
        if (envelope.tags) {
          envelope.tags['ai.cloud.role'] = 'facturalibre-portal';
        }
      });

      this.initialized = true;
      console.log('[AppInsights] Initialized successfully');
    } catch (error) {
      console.error('[AppInsights] Failed to initialize:', error);
    }
  }

  isInitialized(): boolean {
    return this.initialized;
  }

  trackEvent(name: string, properties?: Record<string, string>, measurements?: Record<string, number>): void {
    if (!this.appInsights) return;
    this.appInsights.trackEvent({ name, properties, measurements });
  }

  trackException(error: Error | unknown, properties?: Record<string, string>): void {
    if (!this.appInsights) return;
    const exception = error instanceof Error ? error : new Error(String(error));
    this.appInsights.trackException({
      exception,
      severityLevel: SeverityLevel.Error,
      properties,
    });
  }

  trackPageView(name?: string, properties?: Record<string, string>): void {
    if (!this.appInsights) return;
    this.appInsights.trackPageView({ name, properties });
  }

  trackMetric(name: string, average: number, properties?: Record<string, string>): void {
    if (!this.appInsights) return;
    this.appInsights.trackMetric({ name, average }, properties);
  }

  trackTrace(message: string, severity: SeverityLevel = SeverityLevel.Information, properties?: Record<string, string>): void {
    if (!this.appInsights) return;
    this.appInsights.trackTrace({ message, severityLevel: severity, properties });
  }

  setAuthenticatedUser(userId: string, accountId?: string): void {
    if (!this.appInsights) return;
    this.appInsights.setAuthenticatedUserContext(userId, accountId, true);
  }

  clearAuthenticatedUser(): void {
    if (!this.appInsights) return;
    this.appInsights.clearAuthenticatedUserContext();
  }
}

export const appInsightsService = new AppInsightsService();
export default appInsightsService;
